import { useEffect, useRef } from "react";
import { Terminal } from "@phosphor-icons/react";

const PHASES = [
    "Queued",
    "Searching listings",
    "Parsing search results",
    "Visiting provider websites",
    "Extracting emails",
    "Cleaning records",
    "Preparing CSV",
    "Complete",
];

export default function ProgressLog({ run }) {
    const logEndRef = useRef(null);

    useEffect(() => {
        if (logEndRef.current) {
            logEndRef.current.scrollTop = logEndRef.current.scrollHeight;
        }
    }, [run?.logs?.length]);

    const phaseIdx = Math.max(0, PHASES.indexOf(run?.phase));
    const progress = run?.progress ?? 0;
    const failed = run?.status === "failed";
    const running = run?.status === "running" || run?.status === "queued";

    return (
        <div className="panel" data-testid="progress-panel">
            <div className="panel-header">
                <div className="flex items-center gap-2">
                    <Terminal size={16} weight="bold" />
                    <span className="label-kicker">Live Progress</span>
                    <span
                        className={
                            "dot " +
                            (failed
                                ? "dot-error"
                                : run?.status === "complete"
                                ? "dot-success"
                                : "dot-warning pulse-dot")
                        }
                    />
                    <span className="mono text-xs text-[#52525b]">
                        {run?.status?.toUpperCase()} · {run?.phase}
                    </span>
                </div>
                <span className="mono text-xs">{progress}%</span>
            </div>

            <div className="p-5 flex flex-col gap-4">
                <div className="progress-track" data-testid="progress-bar">
                    <div
                        className="progress-fill"
                        style={{ width: `${progress}%` }}
                    />
                </div>

                <div className="grid grid-cols-4 md:grid-cols-8 gap-1 text-[10px]">
                    {PHASES.map((p, i) => {
                        const active = i === phaseIdx;
                        const done = i < phaseIdx || run?.status === "complete";
                        return (
                            <div
                                key={p}
                                className={
                                    "flex flex-col items-start p-2 border " +
                                    (active
                                        ? "border-black bg-[#fafafa]"
                                        : done
                                        ? "border-[#e4e4e7] bg-white"
                                        : "border-[#e4e4e7] bg-white opacity-60")
                                }
                            >
                                <span
                                    className={
                                        "dot " +
                                        (done
                                            ? "dot-black"
                                            : active
                                            ? "dot-warning pulse-dot"
                                            : "dot-muted")
                                    }
                                />
                                <span className="mono text-[9px] leading-tight break-words">
                                    {p}
                                </span>
                            </div>
                        );
                    })}
                </div>

                <div ref={logEndRef} className="terminal" data-testid="log-terminal">
                    {(run?.logs || []).length === 0 && (
                        <div className="log-line log-caret">waiting for output…</div>
                    )}
                    {(run?.logs || []).map((l, i) => (
                        <div key={`${i}-${(l || "").slice(0, 32)}`} className="log-line log-caret">
                            {l}
                        </div>
                    ))}
                    {running && (
                        <div className="log-line log-caret" style={{ color: "#ffc000" }}>
                            running…
                        </div>
                    )}
                    {run?.error && (
                        <div className="log-line" style={{ color: "#ff3333" }}>
                            ERROR: {run.error}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
